import type { LanguageCode } from '../../components/LanguageFlag';

export type TranslateFn = (key: string, vars?: Record<string, string | number>) => string;

export type StrategyTimeZoneId = 'en-gb-london' | 'de-berlin' | 'vi-hcm' | 'fr-paris';
export type StrategyCategoryId = 'saas-tech' | 'b2b-marketing' | 'fintech' | 'healthcare';

export const LANGUAGE_TIME_ZONES: Record<LanguageCode, StrategyTimeZoneId> = {
  en: 'en-gb-london',
  de: 'de-berlin',
  vi: 'vi-hcm',
  fr: 'fr-paris',
};

export const STRATEGY_TIME_ZONE_OPTIONS: Record<StrategyTimeZoneId, string> = {
  'en-gb-london': '(UTC+00:00) London, United Kingdom',
  'de-berlin': '(UTC+01:00) Berlin, Germany',
  'vi-hcm': '(GMT+7) Ho Chi Minh City, Vietnam',
  'fr-paris': '(UTC+01:00) Paris, France',
};

export const STRATEGY_TIME_ZONE_ORDER: StrategyTimeZoneId[] = [
  LANGUAGE_TIME_ZONES.en,
  LANGUAGE_TIME_ZONES.de,
  LANGUAGE_TIME_ZONES.vi,
  LANGUAGE_TIME_ZONES.fr,
];

const LEGACY_TIME_ZONE_MAP: Record<string, StrategyTimeZoneId> = {
  'gmt+7-hcm': 'vi-hcm',
  'utc+1-eu': 'de-berlin',
  'utc+0-london': 'en-gb-london',
};

export function normalizeStrategyTimeZone(
  value: unknown,
  language: LanguageCode = 'en',
): StrategyTimeZoneId {
  if (typeof value === 'string' && value in STRATEGY_TIME_ZONE_OPTIONS) {
    return value as StrategyTimeZoneId;
  }
  if (typeof value === 'string' && value in LEGACY_TIME_ZONE_MAP) {
    return LEGACY_TIME_ZONE_MAP[value];
  }
  return LANGUAGE_TIME_ZONES[language];
}

export const STRATEGY_CATEGORY_OPTIONS: Record<StrategyCategoryId, string> = {
  'saas-tech': 'SaaS / Technology',
  'b2b-marketing': 'B2B Marketing',
  fintech: 'FinTech',
  healthcare: 'Healthcare',
};

const STRATEGY_TIME_ZONE_LABEL_KEYS: Record<StrategyTimeZoneId, string> = {
  'en-gb-london': 'campaigns.strategy.tzLondon',
  'de-berlin': 'campaigns.strategy.tzBerlin',
  'vi-hcm': 'campaigns.strategy.tzHcm',
  'fr-paris': 'campaigns.strategy.tzParis',
};

const STRATEGY_CATEGORY_LABEL_KEYS: Record<StrategyCategoryId, string> = {
  'saas-tech': 'campaigns.strategy.catSaas',
  'b2b-marketing': 'campaigns.strategy.catB2b',
  fintech: 'campaigns.strategy.catFintech',
  healthcare: 'campaigns.strategy.catHealthcare',
};

export function getStrategyTimeZoneLabel(id: StrategyTimeZoneId, t: TranslateFn): string {
  return t(STRATEGY_TIME_ZONE_LABEL_KEYS[id]);
}

export function getStrategyCategoryLabel(id: StrategyCategoryId, t: TranslateFn): string {
  return t(STRATEGY_CATEGORY_LABEL_KEYS[id]);
}

export type CampaignStrategyData = {
  language: LanguageCode;
  timeZone: StrategyTimeZoneId;
  category: StrategyCategoryId;
  objective: string;
  targetAudience: string;
  toneOfVoice: string[];
  toneDescription: string;
  contentPillars: string[];
  keyMessages: string[];
  brandGuidelines: string;
  hashtagStrategy: string[];
  hashtagNotes: string;
  competitorReferences: string[];
  postingFrequency: string;
  preferredPostingTimes: string[];
};

export type CampaignStrategySnapshot = {
  data: CampaignStrategyData;
  savedAt: string | null;
};

export type StrategyAiPreview = {
  generatedAt: string;
  campaignName: string;
  brandPositioning: string;
  contentPillars: string[];
  toneOfVoice: string[];
  keyMessages: string[];
  hashtagPlan: string[];
};

export type StrategyFieldKey = keyof CampaignStrategyData;

const STORAGE_KEY = 'hawk.campaign.strategy.v1';

export const STRATEGY_LIMITS = {
  objective: 300,
  targetAudience: 300,
  toneDescription: 200,
  brandGuidelines: 500,
  hashtagNotes: 300,
  postingFrequency: 100,
  listItem: 200,
  chipItem: 80,
  maxPillars: 12,
  maxMessages: 12,
  maxHashtags: 24,
  maxTones: 10,
  maxTimes: 10,
  maxCompetitors: 15,
} as const;

const DEFAULT_TONES = ['Professional', 'Friendly', 'Confident'];
const DEFAULT_PILLARS = ['Innovation', 'Product Updates', 'Customer Success', 'Industry Insights', 'Growth'];
const DEFAULT_MESSAGES = [
  'Our product helps you work smarter, not harder.',
  'Trusted by thousands of businesses worldwide.',
  'Continuous innovation for better results.',
  'Committed to customer success.',
];
const DEFAULT_HASHTAGS = ['#Innovation', '#ProductUpdate', '#Growth', '#SaaS', '#BusinessIntelligence'];
const DEFAULT_TIMES = ['09:00 AM', '01:00 PM', '06:00 PM'];
const DEFAULT_COMPETITORS = ['HubSpot', 'Salesforce', 'Monday.com'];

export function createDefaultCampaignStrategy(): CampaignStrategyData {
  return {
    language: 'en',
    timeZone: LANGUAGE_TIME_ZONES.en,
    category: 'saas-tech',
    objective: 'Increase brand awareness and engagement among target audience.',
    targetAudience: 'Marketing professionals, Business owners, SaaS enthusiasts',
    toneOfVoice: [...DEFAULT_TONES],
    toneDescription: 'Professional, approachable, and insight-led.',
    contentPillars: [...DEFAULT_PILLARS],
    keyMessages: [...DEFAULT_MESSAGES],
    brandGuidelines:
      'Use our brand colors and logo consistently. Focus on clarity, innovation, and customer success stories.',
    hashtagStrategy: [...DEFAULT_HASHTAGS],
    hashtagNotes: 'Mix branded and industry hashtags. Limit to 5 per post.',
    competitorReferences: [...DEFAULT_COMPETITORS],
    postingFrequency: '3-4 times per week',
    preferredPostingTimes: [...DEFAULT_TIMES],
  };
}

function readStore(): Record<string, CampaignStrategySnapshot> {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, CampaignStrategySnapshot>;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeStore(store: Record<string, CampaignStrategySnapshot>) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
}

export function readCampaignStrategySnapshot(campaignId: number): CampaignStrategySnapshot {
  const store = readStore();
  const entry = store[String(campaignId)];
  if (!entry?.data) {
    return { data: createDefaultCampaignStrategy(), savedAt: null };
  }
  const merged = { ...createDefaultCampaignStrategy(), ...entry.data };
  return {
    data: {
      ...merged,
      timeZone: normalizeStrategyTimeZone(entry.data?.timeZone, merged.language),
    },
    savedAt: entry.savedAt ?? null,
  };
}

export function writeCampaignStrategySnapshot(campaignId: number, data: CampaignStrategyData): string {
  const savedAt = new Date().toISOString();
  const store = readStore();
  store[String(campaignId)] = { data, savedAt };
  writeStore(store);
  return savedAt;
}

export function formatStrategySavedLabel(
  savedAt: string | null,
  t: TranslateFn,
  localeTag = 'en-GB',
): string {
  if (!savedAt) return t('campaigns.strategy.notSaved');
  const date = new Date(savedAt);
  if (Number.isNaN(date.getTime())) return t('campaigns.strategy.notSaved');
  const formatted = date.toLocaleString(localeTag, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
  return t('campaigns.strategy.lastSaved', { date: formatted });
}

function pushChipError(
  errors: Partial<Record<StrategyFieldKey, string>>,
  field: StrategyFieldKey,
  items: string[],
  itemKey: string,
  maxItems: number,
  maxLen: number,
  t: TranslateFn,
) {
  const item = t(itemKey);
  if (items.length === 0) {
    errors[field] = t('campaigns.strategy.errAddOne', { item });
    return;
  }
  if (items.length > maxItems) {
    errors[field] = t('campaigns.strategy.errMaxItems', { n: maxItems, item });
    return;
  }
  const tooLong = items.find((item) => item.length > maxLen);
  if (tooLong) errors[field] = t('campaigns.strategy.errItemMaxLen', { item, n: maxLen });
}

export function validateCampaignStrategy(
  data: CampaignStrategyData,
  t: TranslateFn,
): Partial<Record<StrategyFieldKey, string>> {
  const errors: Partial<Record<StrategyFieldKey, string>> = {};

  if (!data.objective.trim()) errors.objective = t('campaigns.strategy.errObjectiveRequired');
  else if (data.objective.length > STRATEGY_LIMITS.objective) {
    errors.objective = t('campaigns.strategy.errMaxChars', { n: STRATEGY_LIMITS.objective });
  }

  if (!data.targetAudience.trim()) errors.targetAudience = t('campaigns.strategy.errAudienceRequired');
  else if (data.targetAudience.length > STRATEGY_LIMITS.targetAudience) {
    errors.targetAudience = t('campaigns.strategy.errMaxChars', { n: STRATEGY_LIMITS.targetAudience });
  }

  if (!data.brandGuidelines.trim()) errors.brandGuidelines = t('campaigns.strategy.errBrandRequired');
  else if (data.brandGuidelines.length > STRATEGY_LIMITS.brandGuidelines) {
    errors.brandGuidelines = t('campaigns.strategy.errMaxChars', { n: STRATEGY_LIMITS.brandGuidelines });
  }

  if (data.toneDescription.length > STRATEGY_LIMITS.toneDescription) {
    errors.toneDescription = t('campaigns.strategy.errMaxChars', { n: STRATEGY_LIMITS.toneDescription });
  }

  if (data.hashtagNotes.length > STRATEGY_LIMITS.hashtagNotes) {
    errors.hashtagNotes = t('campaigns.strategy.errMaxChars', { n: STRATEGY_LIMITS.hashtagNotes });
  }

  if (!data.postingFrequency.trim()) errors.postingFrequency = t('campaigns.strategy.errFrequencyRequired');
  else if (data.postingFrequency.length > STRATEGY_LIMITS.postingFrequency) {
    errors.postingFrequency = t('campaigns.strategy.errMaxChars', { n: STRATEGY_LIMITS.postingFrequency });
  }

  pushChipError(errors, 'toneOfVoice', data.toneOfVoice, 'campaigns.strategy.itemTone', STRATEGY_LIMITS.maxTones, STRATEGY_LIMITS.chipItem, t);
  pushChipError(errors, 'contentPillars', data.contentPillars, 'campaigns.strategy.itemPillar', STRATEGY_LIMITS.maxPillars, STRATEGY_LIMITS.listItem, t);
  pushChipError(errors, 'keyMessages', data.keyMessages, 'campaigns.strategy.itemMessage', STRATEGY_LIMITS.maxMessages, STRATEGY_LIMITS.listItem, t);
  pushChipError(errors, 'hashtagStrategy', data.hashtagStrategy, 'campaigns.strategy.itemHashtag', STRATEGY_LIMITS.maxHashtags, STRATEGY_LIMITS.chipItem, t);
  pushChipError(
    errors,
    'preferredPostingTimes',
    data.preferredPostingTimes,
    'campaigns.strategy.itemTime',
    STRATEGY_LIMITS.maxTimes,
    STRATEGY_LIMITS.chipItem,
    t,
  );
  pushChipError(
    errors,
    'competitorReferences',
    data.competitorReferences,
    'campaigns.strategy.itemCompetitor',
    STRATEGY_LIMITS.maxCompetitors,
    STRATEGY_LIMITS.chipItem,
    t,
  );

  return errors;
}

export function hasStrategyValidationErrors(errors: Partial<Record<StrategyFieldKey, string>>): boolean {
  return Object.keys(errors).length > 0;
}

export function strategiesAreEqual(left: CampaignStrategyData, right: CampaignStrategyData): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

type CampaignMeta = {
  id: number;
  name: string;
  type: string;
  status: string;
};

export async function generateStrategyAiPreview(campaign: CampaignMeta): Promise<StrategyAiPreview> {
  await new Promise((resolve) => {
    window.setTimeout(resolve, 1200);
  });

  const focus = campaign.type.toLowerCase().includes('awareness') ? 'brand visibility' : 'pipeline growth';

  return {
    generatedAt: new Date().toISOString(),
    campaignName: campaign.name,
    brandPositioning: `Position ${campaign.name} as a trusted ${focus} partner for B2B teams, emphasizing practical outcomes over hype.`,
    contentPillars: [
      'Thought leadership',
      'Product proof points',
      'Customer proof',
      'Industry trends',
      'Team & culture',
    ],
    toneOfVoice: ['Professional', 'Confident', 'Helpful'],
    keyMessages: [
      'We help teams ship faster with less friction.',
      'Trusted by operators who need measurable impact.',
      'Built for modern B2B workflows.',
    ],
    hashtagPlan: ['#B2BSaaS', '#Growth', '#Productivity', '#CustomerSuccess', campaign.name.replace(/\s+/g, '')],
  };
}

export function formatStrategyAiPreview(preview: StrategyAiPreview, t: TranslateFn): string {
  return [
    t('campaigns.strategy.previewBrand', { text: preview.brandPositioning }),
    t('campaigns.strategy.previewPillars', { text: preview.contentPillars.join(', ') }),
    t('campaigns.strategy.previewTone', { text: preview.toneOfVoice.join(', ') }),
    t('campaigns.strategy.previewMessages', { text: preview.keyMessages.join(' | ') }),
    t('campaigns.strategy.previewHashtags', { text: preview.hashtagPlan.join(' ') }),
  ].join('\n');
}

export function formatStrategyContextForAi(data: CampaignStrategyData, campaignName: string): string {
  return [
    `Campaign: ${campaignName}`,
    `Language: ${data.language}`,
    `Time zone: ${STRATEGY_TIME_ZONE_OPTIONS[data.timeZone]}`,
    `Category: ${STRATEGY_CATEGORY_OPTIONS[data.category]}`,
    `Objective: ${data.objective}`,
    `Audience: ${data.targetAudience}`,
    `Tone: ${data.toneOfVoice.join(', ')}${data.toneDescription ? ` — ${data.toneDescription}` : ''}`,
    `Pillars: ${data.contentPillars.join(', ')}`,
    `Key messages: ${data.keyMessages.join(' | ')}`,
    `Brand guidelines: ${data.brandGuidelines}`,
    `Hashtags: ${data.hashtagStrategy.join(' ')}`,
    `Competitors: ${data.competitorReferences.join(', ')}`,
    `Posting: ${data.postingFrequency} at ${data.preferredPostingTimes.join(', ')}`,
  ].join('\n');
}
